import { Skeleton } from "../../elements/skeleton/skeleton";
import { Container, type ContainerProps } from "../../layout/grid";
import { cn } from "../../lib/utils";

// Kept in sync with geometryTableVariants in geometry-table.tsx. That module is
// a client component, so its values cannot be imported into this server-safe one.
const COLUMN_WIDTH_CLASSES = {
  8: "col-span-12 md:col-span-8 md:col-start-3",
  10: "col-span-12 md:col-span-10 md:col-start-2",
  12: "col-span-12",
};

const BACKGROUNDS = {
  black: "bg-black",
  white: "bg-white",
  whitesmoke: "bg-whitesmoke",
};

const DEFAULT_COLUMN_WIDTH = 8;
const SIZE_COLUMNS = 5;
const MEASUREMENT_ROWS = 10;

function keys(count: number, prefix: string) {
  return Array.from({ length: count }, (_, index) => `${prefix}-${index}`);
}

export interface GeometryTableSkeletonProps {
  anchorId?: string;
  backgroundColor?: "black" | "white" | "whitesmoke";
  columnWidth?: 8 | 10 | 12 | null;
  paddingTop?: ContainerProps["paddingTop"];
  paddingBottom?: ContainerProps["paddingBottom"];
  showUnitToggle?: boolean;
  /** Announced to assistive technology while the table loads. */
  loadingLabel?: string;
}

/**
 * Placeholder for GeometryTable while its geometry data loads: the frame
 * diagram, the unit toggle and the measurement table.
 */
export function GeometryTableSkeleton(
  props: Readonly<GeometryTableSkeletonProps>
) {
  const showUnitToggle = props.showUnitToggle ?? true;

  return (
    <Container
      className={cn(
        "component-geometry-table-skeleton",
        props.backgroundColor ? BACKGROUNDS[props.backgroundColor] : undefined
      )}
      fullWidth
      id={props.anchorId}
      paddingBottom={props.paddingBottom}
      paddingTop={props.paddingTop}
      testId="geometry-table-skeleton"
    >
      <div
        aria-busy="true"
        className={cn(
          "block min-w-0",
          COLUMN_WIDTH_CLASSES[props.columnWidth ?? DEFAULT_COLUMN_WIDTH]
        )}
        data-skeleton="geometry-table"
      >
        {props.loadingLabel ? (
          <output className="sr-only">{props.loadingLabel}</output>
        ) : null}
        <div className="mb-8 flex justify-center">
          <Skeleton className="aspect-[750/547] h-auto w-full md:max-w-3xl" />
        </div>

        {showUnitToggle ? (
          <div
            className="mb-2 flex items-center justify-end gap-3 md:mb-10"
            data-testid="geometry-table-unit-toggle-skeleton"
          >
            <Skeleton className="h-6 w-20" />
            <Skeleton className="h-11 w-39 rounded-sm" />
          </div>
        ) : null}

        <div className="flex border-stone-200 border-b md:hidden">
          {keys(SIZE_COLUMNS, "tab").map((key) => (
            <div className="flex size-14 items-center justify-center" key={key}>
              <Skeleton className="h-5 w-8" />
            </div>
          ))}
        </div>

        <div className="divide-y divide-stone-200">
          <div className="hidden gap-4 py-3 md:flex">
            <Skeleton className="h-5 w-1/4" />
            {keys(SIZE_COLUMNS, "head").map((key) => (
              <Skeleton className="h-5 flex-1" key={key} />
            ))}
          </div>
          {keys(MEASUREMENT_ROWS, "row").map((key) => (
            <div
              className="flex gap-4 py-3"
              data-testid="geometry-table-row-skeleton"
              key={key}
            >
              <Skeleton className="h-5 w-1/2 md:w-1/4" />
              {keys(SIZE_COLUMNS, `${key}-cell`).map((cellKey, index) => (
                <Skeleton
                  className={cn("h-5 flex-1", index > 0 && "hidden md:block")}
                  key={cellKey}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    </Container>
  );
}
